import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "store/useSelector";

import type * as types from "../types/types";
import {
  askrClients,
  PRODUCTION_SEGMENTS_QUERY,
} from "../providers/AskrProvider";
import { findGaps, findIntersecting } from "../utils/segments";
import { ONE_DAY_OF_MS, ONE_MINUTE_OF_MS } from "../utils/timeUtils";
import { FETCH_POLICY } from "./AggregatesGetter/aggregatesGetterUtils";
import { DEFAULT_SEGMENTS_GETTER_UPDATE_INTERVAL } from "../constants/intervals";
import { useHandleError } from "contexts/ErrorManager/ErrorManager";
import { useFocusRange } from "utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useMount } from "constants/constants";
import { isEqual } from "lodash";
import { useExtraQueryOptions } from "utils/useQuery";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useProcessQueryQueue, useQueryQueue } from "utils/queryQueue";
import { useInterval } from "react-use";

const POLLING_PADDING = 5 * ONE_MINUTE_OF_MS;
const MAX_BACKOFF = 10 * 60 * 1000;
const INITIAL_BACKOFF = 5 * 1000;
const MAX_QUERY_RANGE = 6 * ONE_DAY_OF_MS;

// The props we expect to be passed to the exported component.
interface SegmentsGetterProps {
  process: types.Process;
  updatePollingInterval?: number;
  queuedSegmentsRef: React.MutableRefObject<
    { processId: string; segments: types.ProductionSegmentType[] }[]
  >;
}

const nextBackoff = (backoff: number) =>
  Math.min(1.5 * (backoff + 1000), MAX_BACKOFF);

const isNoChangeInSegments = (
  segments: types.ProductionSegmentType[],
  prevSegments: types.ProductionSegmentType[]
) => {
  if (segments.length === 0) {
    return true;
  } else if (segments.length > 0 && prevSegments.length > 0) {
    const prevTail = prevSegments[prevSegments.length - 1];
    const { ge: prevGe, lt: prevLt } = prevTail.range;
    const tail = segments[segments.length - 1];
    const { ge, lt } = tail.range;
    const noChange =
      prevGe === ge &&
      prevLt === lt &&
      tail.pnl &&
      prevTail.pnl &&
      prevTail.pnl.category === tail.pnl.category &&
      prevTail.events?.length === tail.events?.length &&
      isEqual(prevTail.meta, tail.meta);
    if (noChange) {
      return true;
    }
  }
  return false;
};

const getInfillQueryVariables = (
  gaps: types.TimeRangeFinite[],
  processId: string,
  ge: number,
  lt: number,
  lru: types.TimeRangeFinite[],
  maxQueryRange = MAX_QUERY_RANGE
) => {
  // just try the first gap
  let gapGe: number = +gaps[0].ge;
  let gapLt: number = +gaps[0].lt;

  // Don't bother trying to fill gaps into the future
  const now = Date.now();
  if (gapLt > now + POLLING_PADDING) {
    gapLt = now + POLLING_PADDING;
  }

  // Make sure the query range is not larger than the max allowable
  // range and prefer filling in data inside the [ge, lt) range.
  const dGap = gapLt - gapGe;
  const overMax = dGap - maxQueryRange;
  if (overMax > 0) {
    // * I'm sure we can do a better job shrinking only as much as needed
    gapGe = Math.max(gapGe, ge);
    gapLt = Math.min(gapLt, lt);
  }

  // If still too large, prefer filling in the most recent data
  // first (move ge, not lt).
  gapGe = Math.max(gapGe, gapLt - maxQueryRange);

  // * look at better LRU/loop-breaking options
  // Check if we are potentially repeating a recent request, if so
  // adjust the request range.
  for (const r of lru) {
    if (r.ge === gapGe && r.lt === gapLt) {
      gapLt = lt;
      gapGe = Math.max(ge, lt - maxQueryRange);
      break;
    }
  }

  // Update the LRU and only keep the last couple requests
  lru.push({ ge: gapGe, lt: gapLt });
  while (lru.length > 2) {
    lru.shift();
  }

  return {
    machemetrySourceName: processId,
    ge: gapGe.toString(),
    lt: gapLt.toString(),
  };
};

const baseProductionSegmentsQuery = {
  query: PRODUCTION_SEGMENTS_QUERY,
  fetchPolicy: FETCH_POLICY,
};
const SegmentsGetter = ({
  process,
  updatePollingInterval = DEFAULT_SEGMENTS_GETTER_UPDATE_INTERVAL,
  queuedSegmentsRef,
}: // tslint:disable-next-line
SegmentsGetterProps) => {
  // Set up query queueing system
  const { enqueueQuery } = useQueryQueue();
  const { processQueue } = useProcessQueryQueue(1000); // Process queue every 1 second

  // Track when we process queries from the queue
  // Track when we need to process queued queries
  const [queueTrigger, setQueueTrigger] = useState(0);

  // Process the query queue on a fixed interval
  useInterval(() => {
    const { queriesToProcess } = processQueue();
    // If we have segments queries to process for this process
    const segmentsQueries = queriesToProcess.filter(
      q => q.type === "productionSegments" && q.id === process.id
    );
    if (segmentsQueries.length > 0) {
      setQueueTrigger(prev => prev + 1);
    }
  }, 1000); // Process queue every 1 second
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  const focusRange = useFocusRange();
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const segmentsFromStore: types.ManyProductionSegmentsArrays = useSelector(
    state => state.legacy.segments
  );
  const smallestGe =
    visibleRange && focusRange
      ? Math.min(visibleRange.ge, focusRange.ge)
      : visibleRange?.ge || focusRange?.ge || null;

  const biggestLt =
    visibleRange && focusRange
      ? Math.max(visibleRange.lt, focusRange.lt)
      : visibleRange?.lt || focusRange?.lt || null;

  const processSegments = segmentsFromStore[process.id];
  const ge = smallestGe;
  const lt = biggestLt;
  const segments = useMemo(
    () =>
      processSegments && processSegments.length > 0 ? processSegments : [],
    [processSegments]
  );

  const askrClient = askrClients.get(process.ckey);
  const visibleSegments = useMemo(
    () => (ge && lt ? findIntersecting(segments, ge, lt) : []),
    [segments, ge, lt]
  );

  const requestLru = useRef([] as types.TimeRangeFinite[]);
  const prevLt = useRef(lt);
  const pollTimer = useRef(null as number | null);
  const handleError = useHandleError();
  const isStorybook = useIsStorybook();

  const queueNewSegments = useCallback(
    ({ processId, newSegments }) => {
      queuedSegmentsRef.current = [
        ...queuedSegmentsRef.current,
        { processId, segments: newSegments },
      ];
    },
    [queuedSegmentsRef]
  );

  const extraQueryOptions = useExtraQueryOptions();

  // Regularly poll for new data. This effect will regularly check for
  // new segments from askr, and dispatch updates. If the current view
  // (ge, lt) is very much before now, this effect will not poll, but it
  // will continue checking to see if it should poll.
  useEffect(() => {
    if (!ge || !lt || isStorybook) {
      return;
    }
    const seglen = segments.length;
    const haveSegmentsInStore = seglen > 0;

    const doPoll = () => {
      const now = Date.now();
      const focusRangeEndPlusBuffer = lt + ONE_DAY_OF_MS;
      const isCurrentShiftOrCloseEnough = focusRangeEndPlusBuffer > now;

      // only poll for segments during current or current-ish time ranges
      if (isCurrentShiftOrCloseEnough && haveSegmentsInStore) {
        const nextLt = Date.now() + POLLING_PADDING;
        const idealNextGe = segments[seglen - 1].range.ge || ge;
        const nextGe = Math.max(
          idealNextGe - POLLING_PADDING,
          nextLt - MAX_QUERY_RANGE
        );
        const variables = {
          machemetrySourceName: process.id,
          ge: nextGe.toString(),
          lt: nextLt.toString(),
        };
        // don't send requests with 0s

        if (Boolean(variables.ge) && Boolean(variables.lt)) {
          // Instead of querying directly, enqueue the query for processing
          // This throttles the queries when focus range changes rapidly
          enqueueQuery({
            id: process.id,
            type: "productionSegments",
            variables,
            extraData: {
              headers: {
                ...baseProductionSegmentsQuery,
                ...extraQueryOptions,
              },
              processId: process.id,
            },
          });
        }
      }

      // don't set a timer if there's already one ticking
      if (!pollTimer.current) {
        pollTimer.current = window.setTimeout(() => {
          // if the timer hasn't fired...
          if (pollTimer.current) {
            // the timer has fired
            pollTimer.current = null;
            // fire!
            doPoll();
          }
        }, updatePollingInterval * multiplyNetworkRequestIntervals);
      }
    };
    // Figure out if we just jumped back to the range containing now.
    // This lets us catch catch up quickly when navigating back to the
    // head of the timeline after looking at historical data. However,
    // don't poll on the intial render, instead, let the infill handle
    // getting the initial data.
    const now2 = Date.now();
    const justJumpedBackToCurrentRange =
      !prevLt.current || (prevLt.current < now2 && now2 <= lt);
    if (justJumpedBackToCurrentRange) {
      prevLt.current = lt;
      doPoll();
    }

    // Check to see if we need to poll to continuously update. We only
    // need to poll if the if the last segment starts within the
    // considered range, and that segment is open on the right.
    if (haveSegmentsInStore) {
      const lastSeg = segments.slice(-1)[0];
      const { ge: lastGe, lt: lastLt } = lastSeg.range;
      const isLastSegmentInRangeAndOpenOnRight =
        lastGe && +lastGe < lt && lastLt === null;
      if (
        isLastSegmentInRangeAndOpenOnRight &&
        // don't set a timer if there's already one ticking
        !pollTimer.current
      ) {
        // If the last segment is open we need to poll to see if it gets
        // updated, otherwise, if it is closed we can safely not poll.

        pollTimer.current = window.setTimeout(() => {
          // if the timer hasn't fired...
          if (pollTimer.current) {
            // the timer has fired
            pollTimer.current = null;
            // fire!
            doPoll();
          }
        }, updatePollingInterval * multiplyNetworkRequestIntervals);
      }
    }
  }, [
    ge,
    lt,
    isStorybook,
    segments,
    enqueueQuery,
    process.id,
    extraQueryOptions,
    multiplyNetworkRequestIntervals,
    updatePollingInterval,
  ]);

  // clear any timers that haven't fired on unmount
  useMount(() => {
    return () => {
      if (pollTimer.current) {
        window.clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
    };
  });

  const backoff = useRef(INITIAL_BACKOFF);
  const awaitingInfill = useRef(false);
  const [maxQueryRange, setMaxQueryRange] = useState(MAX_QUERY_RANGE);
  // reset maxQueryRange when focusRange changes
  useEffect(() => {
    setMaxQueryRange(MAX_QUERY_RANGE);
  }, [focusRange]);
  useEffect(() => {
    let timer = null as number | null;

    // In this first section we test to see if the infill effect is
    // already trying to get data, and if not, we check to see if there
    // are gaps that need to be filled.
    if (awaitingInfill.current || isStorybook || !(ge && lt)) {
      return;
    }

    const gaps = findGaps(visibleSegments, +ge, +lt);
    if (gaps.length === 0) {
      return;
    }

    (async () => {
      const variables = getInfillQueryVariables(
        gaps,
        process.id,
        ge,
        lt,
        requestLru.current,
        maxQueryRange
      );
      const isSuccessfulInfill = async () => {
        awaitingInfill.current = true;
        let success = false;

        await askrClient
          .query({
            ...baseProductionSegmentsQuery,
            ...extraQueryOptions,
            variables,
          })
          .then(
            resp => {
              if (!resp.data.productionSegments) {
                // would like to check for "too many segments" e.g. resp.data.errors?.[0]?.message.includes("too many segments") but it's not populated at the time this runs; only seems to populate slightly later (i.e. if you console.log(resp.data.errors) it's empty at first, then populates)
                // so for now, assume that if there are no segments, it's because there are too many
                console.log(
                  `Too many segments? returned for ${process.id} (${
                    process.name
                  }) -> trying again with max query range of ${
                    maxQueryRange / 2 / ONE_DAY_OF_MS
                  } days`
                );
                setMaxQueryRange(prev => prev / 2);
                awaitingInfill.current = false;
                return;
              }

              awaitingInfill.current = false;

              // Now check to see if there is a difference between the
              // segments that were just returned, and the ones we already
              // have. For example, if we got an empty list back we know
              // that we can't possibly have filled the gap.
              // TECHDEBT: Remove this goodSegments check once we fix this issue in askr
              const goodSegments = resp.data.productionSegments.filter(
                s =>
                  s.pnl !== null &&
                  (s.range.ge === null ||
                    s.range.lt === null ||
                    +s.range.ge < +s.range.lt)
              );
              let isAtLeastOneSegmentNew = false;
              for (const s of goodSegments) {
                // TECHDEBT: switch to bisection search since it's an ordered list
                const si = segments.findIndex(
                  ({ range }) =>
                    s.range.ge === range.ge && s.range.lt === range.lt
                );
                if (si === -1) {
                  isAtLeastOneSegmentNew = true;
                  break;
                }
              }
              if (isAtLeastOneSegmentNew) {
                // If there is new data reset the backoff and dispatch the
                // new segments to the store. We set success to true but the
                // re-render that will be cause by the store update also kind
                // of handles that.
                backoff.current = INITIAL_BACKOFF;
                success = true;
                queueNewSegments({
                  processId: process.id,
                  newSegments: goodSegments,
                });
              }
              // Treat failing to fill the gap as a failure. Plan to try again.
              backoff.current = nextBackoff(backoff.current);
            },
            error => {
              awaitingInfill.current = false;
              backoff.current = nextBackoff(backoff.current);
              console.error(error);
            }
          );
        return success;
      };

      // Repeatedly try to fill the gap until either it is successfully
      // filled, or until this effect is cleaned up (e.g., on re-render
      // because there is new data, or the visible window has changed).
      const sleepBeforeBackoff = (ms: number) => {
        return new Promise(resolve => {
          // Keep the timer around so it can be properly cleaned up
          timer = window.setTimeout(resolve, ms);
        });
      };

      while (!isSuccessfulInfill()) {
        console.log(
          `Infill ${process.name}/${process.id} failed, retry in ${backoff.current}ms`
        );
        // While sleeping the effect can get cleaned up and recreated
        // and the backoff will persist until a successful infill happens.
        // Similarly, if an infill request is already in-flight to askr
        // another won't be created, but, because the in-flight will
        // either trigger a render after dispatch, or a retry after
        // failure, we should be ok.
        await sleepBeforeBackoff(backoff.current);
      }
    })();

    return () => {
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [
    visibleSegments,
    segments,
    ge,
    lt,
    askrClient,
    process.id,
    process.name,
    isStorybook,
    extraQueryOptions,
    queueNewSegments,
    maxQueryRange,
  ]);

  // Process queued queries when queueTrigger changes
  useEffect(() => {
    if (queueTrigger === 0 || !askrClient || !process.id) {
      return;
    }

    // Get the current queue snapshot for this component
    const { queriesToProcess } = processQueue();
    const segmentQueries = queriesToProcess.filter(
      q => q.type === "productionSegments" && q.id === process.id
    );

    if (segmentQueries.length === 0) {
      return;
    }

    // Execute each query (Just take the latest one for this process)
    const latestQuery = segmentQueries[segmentQueries.length - 1];
    if (!latestQuery.variables || !latestQuery.extraData) {
      return;
    }

    const { variables, extraData } = latestQuery;
    const { headers, processId } = extraData as {
      headers: any;
      processId: string;
    };

    // Execute the query
    askrClient
      .query({
        ...headers,
        variables,
      })
      .then(
        resp => {
          if (
            resp.data.productionSegments &&
            !isNoChangeInSegments(resp.data.productionSegments, segments)
          ) {
            queueNewSegments({
              processId,
              newSegments: resp.data.productionSegments,
            });
          }
        },
        err => {
          handleError(err, {
            ...headers,
            queryName: "PRODUCTION_SEGMENTS",
            variables,
          });
        }
      );
  }, [
    queueTrigger,
    askrClient,
    segments,
    handleError,
    process.id,
    queueNewSegments,
    processQueue,
  ]);

  return null;
};

export default React.memo(SegmentsGetter, isEqual);
